import { Vector3 } from '@/math'
import type { Surface } from '@/core/surface'
import type { Hit } from '@/voxelTypes'
import { Position } from '@/voxelMath'
import { parseKeyX, parseKeyY, parseKeyZ } from '@/voxelMath'
import { compileSceneDsl } from '@/dsl/compile'
import { blendMutable } from '@/events/layerSystem'
import type { VoxelMap, VoxelData } from '@/voxelTypes'

export type Occupancy2D = {
  width: number
  height: number
  bitset: Uint8Array // row-major: j in [0..height), i in [0..width)
}

export function createOccupancy(width: number, height: number): Occupancy2D {
  const bytes = Math.ceil((width * height) / 8)
  return { width, height, bitset: new Uint8Array(bytes) }
}

export function setOccupied(occ: Occupancy2D, i: number, j: number, v: boolean) {
  if (i < 0 || j < 0 || i >= occ.width || j >= occ.height) return
  const idx = j * occ.width + i
  const byte = idx >> 3
  const bit = idx & 7
  if (v) occ.bitset[byte] |= (1 << bit)
  else occ.bitset[byte] &= ~(1 << bit)
}

export function isOccupied(occ: Occupancy2D, i: number, j: number): boolean {
  if (i < 0 || j < 0 || i >= occ.width || j >= occ.height) return false
  const idx = j * occ.width + i
  const byte = idx >> 3
  const bit = idx & 7
  return ((occ.bitset[byte] >> bit) & 1) === 1
}

export function forEachOccupied(occ: Occupancy2D, fn: (i: number, j: number) => void) {
  const { width: w, height: h, bitset } = occ
  let idx = 0
  for (let j = 0; j < h; j++) {
    for (let i = 0; i < w; i++, idx++) {
      const byte = idx >> 3
      const bit = idx & 7
      if (((bitset[byte] >> bit) & 1) === 1) fn(i, j)
    }
  }
}

// Build occupancy from a surface definition and bounds (XZ extents)
export function occupancyFromSurface(surface: Surface | undefined, bounds: Position): Occupancy2D | null {
  if (!surface) return null
  const w = bounds.x | 0
  const h = bounds.z | 0
  const occ = createOccupancy(w, h)
  if (surface.kind === 'scene') {
    const acc: VoxelMap = new Map<number, VoxelData>()
    const { events } = compileSceneDsl(surface.scene)
    for (const ev of events) {
      if (ev[0] !== 'voxel.blend') continue
      const [, voxels, mode] = ev as any
      blendMutable(acc, voxels as any, mode as any)
    }
    const plane = surface.plane || 'xz'
    // Compute extents to determine origin offset
    let minI = Infinity, minJ = Infinity
    ;(acc as any).forEach?.((_: VoxelData, key: number) => {
      const x = parseKeyX(key), y = parseKeyY(key), z = parseKeyZ(key)
      let i = 0, j = 0
      if (plane === 'xz') { i = x; j = z }
      else if (plane === 'xy') { i = x; j = y }
      else { i = y; j = z }
      if (i < minI) minI = i
      if (j < minJ) minJ = j
    })
    const pad = (surface as any).padding
    const left = (typeof pad === 'number') ? Math.max(0, Math.floor(pad)) : Math.max(0, Math.floor(pad?.left ?? 0))
    const top  = (typeof pad === 'number') ? Math.max(0, Math.floor(pad)) : Math.max(0, Math.floor(pad?.top  ?? 0))
    ;(acc as any).forEach?.((_: VoxelData, key: number) => {
      const x = parseKeyX(key), y = parseKeyY(key), z = parseKeyZ(key)
      let i = 0, j = 0
      if (plane === 'xz') { i = x; j = z }
      else if (plane === 'xy') { i = x; j = y }
      else { i = y; j = z }
      const ii = (i - (minI === Infinity ? 0 : minI)) + left
      const jj = (j - (minJ === Infinity ? 0 : minJ)) + top
      if (ii >= 0 && ii < w && jj >= 0 && jj < h) setOccupied(occ, ii, jj, true)
    })
    return occ
  }
  if (surface.kind === 'ascii') {
    const lines: ReadonlyArray<string> = Array.isArray(surface.lines)
      ? surface.lines as ReadonlyArray<string>
      : String(surface.lines).split(/\r?\n/)
    const at = Array.isArray(surface.at) ? surface.at as ReadonlyArray<number> : undefined
    const offX = at && at.length >= 2 && Number.isFinite(at[0]) ? (at[0] | 0) : 0
    const offZ = at && at.length >= 2 && Number.isFinite(at[1]) ? (at[1] | 0) : 0
    const occupiedSet: Set<string> | null = (typeof surface.occupied === 'string' && surface.occupied.length > 0)
      ? new Set(surface.occupied.split(''))
      : null
    for (let j = 0; j < lines.length; j++) {
      const line = lines[j] ?? ''
      for (let i = 0; i < line.length; i++) {
        const ch = line[i]
        const isOcc = occupiedSet
          ? occupiedSet.has(ch)
          : (ch !== '.' && ch.trim() !== '')
        if (isOcc) setOccupied(occ, offX + i, offZ + j, true)
      }
    }
    return occ
  }
  // Future: mask-based surfaces
  return null
}

export function nearestOccupancyHit(
  occ: Occupancy2D,
  seedX: number,
  seedZ: number,
  intersection: Vector3,
  bounds: Position,
  rayOrigin: Vector3
): Hit | null {
  const width = occ.width
  const height = occ.height
  if (width === 0 || height === 0) return null
  const clampX = (i: number) => Math.max(0, Math.min(width - 1, i))
  const clampZ = (j: number) => Math.max(0, Math.min(height - 1, j))
  const baseX = clampX(seedX)
  const baseZ = clampZ(seedZ)
  const epsilon = 0.0001
  const y = Math.max(0, Math.min(bounds.y - 1, Math.floor(intersection.y + epsilon)))

  const makeHit = (i: number, j: number): Hit => {
    const exactPoint = new Vector3(i + 0.5, y, j + 0.5)
    return {
      position: { x: i, y, z: j },
      normal: new Vector3(0, 1, 0),
      exactPoint,
      type: 'plane',
      distance: rayOrigin.distanceTo(exactPoint),
    }
  }

  if (isOccupied(occ, baseX, baseZ)) {
    return makeHit(baseX, baseZ)
  }

  const maxRadius = Math.max(width, height)
  for (let radius = 1; radius <= maxRadius; radius++) {
    let bestInRing: { hit: Hit; distSq: number } | null = null
    for (let i = baseX - radius; i <= baseX + radius; i++) {
      if (i < 0 || i >= width) continue
      for (let j = baseZ - radius; j <= baseZ + radius; j++) {
        if (j < 0 || j >= height) continue
        if (Math.abs(i - baseX) !== radius && Math.abs(j - baseZ) !== radius) continue
        if (!isOccupied(occ, i, j)) continue
        const cx = i + 0.5
        const cz = j + 0.5
        const dx = cx - intersection.x
        const dz = cz - intersection.z
        const distSq = dx * dx + dz * dz
        if (!bestInRing || distSq < bestInRing.distSq) {
          bestInRing = {
            hit: makeHit(i, j),
            distSq,
          }
        }
      }
    }
    if (bestInRing) {
      return bestInRing.hit
    }
  }

  return null
}
